import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { z } from 'zod';

import { type EnhancedToolResult } from '../types';
import { findAccessibleTemplate } from './accessibleTemplates';
import { Tool, type ToolConstructorArgs } from './Tool';
import { VideoStateTool } from './VideoStateTool';

import type { ToolExecutionContext } from 'js/src/agent/types';
import { captureVideoState, formatInitialStateAsGitDiff } from 'js/src/agent/video-state';
import { AspectRatio, GlobalTemplate } from 'js/src/client';
import { selectSketchControlsBrandKitId } from 'js/src/data/sketch-controls/sketch-controls-selectors';
import { checkpointVideo } from 'js/src/data/video-data';
import { switchLuminaryTemplateInPlace } from 'js/src/data/video-planner';
import { loadAndApplyBrandKitToLuminary } from 'js/src/luminary/brand-kit-operations';
import { markScenesBusy, startSceneBuilding } from 'js/src/data/agent-activity';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import {
    isLuminaryEmpty,
    isSceneAnOverlay,
    isVoiceDrivenSceneGroup,
} from 'js/src/luminary/luminary-selectors';
import { saveRawLuminary } from 'js/src/luminary/utils';

export const rebuildVideoSchema = z.object({
    // A free string, not an enum of the prompt templates: the agent may switch to a
    // template it found with find_templates, and execute() validates against the full
    // catalog (tools run with strict:false, so an enum would only be a misleading hint).
    template_id: z.string().optional(),
    aspect_ratio: z.enum(AspectRatio).optional(),
});
export type RebuildVideoArgs = z.infer<typeof rebuildVideoSchema>;

interface RebuildVideoToolConstructorArgs extends ToolConstructorArgs {
    /** Returns the app's current videoId, or null if no video is loaded. */
    getCurrentVideoId?: () => number | null;
}

/**
 * Rebuilds the current video in place — optionally switching template or aspect ratio — by clearing
 * it to a single blank scene on the chosen template, keeping the same video id/tab. The agent then
 * recreates the content (procedure lives in the `change-template` skill).
 *
 * This is the sanctioned way to switch templates or start over: `create_blank_video` refuses when a
 * video already exists and `delete_scene` refuses to remove the last scene, so
 * `switchLuminaryTemplateInPlace` is the only path that never persists a zero-scene video.
 */
export class RebuildVideoTool extends Tool<
    typeof rebuildVideoSchema,
    RebuildVideoToolConstructorArgs
> {
    protected override readonly schema = rebuildVideoSchema;
    protected override readonly toolName = TOOL_NAMES.rebuildVideo;
    public override readonly subagentAccess = SubagentAccess.UNAVAILABLE;
    protected override readonly toolDescription = `Rebuild the current video in place to switch templates or start it over. Load the ${SKILL_NAMES.changeTemplate} skill for how to use it.`;

    private getCurrentVideoId?: () => number | null;

    public constructor(args: RebuildVideoToolConstructorArgs) {
        super(args);
        this.getCurrentVideoId = args.getCurrentVideoId;
    }

    public getToolDisplayName(): string {
        return 'Rebuilding video';
    }

    public get statusIcon(): string {
        return 'autorenew';
    }

    public async execute(
        args: RebuildVideoArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        // A video exists to rebuild if the app has a persisted videoId OR the agent has
        // already built content on the in-memory luminary this turn (create_blank_video +
        // scenes, before the video is persisted at turn end). Gating on videoId alone would
        // wrongly refuse mid-turn — e.g. switching templates right after adding a recording.
        const hasInMemoryVideo = this.luminary.scenes.length > 0;
        if (this.getCurrentVideoId?.() == null && !hasInMemoryVideo) {
            return {
                result: `Error: there is no video yet, so there is nothing to rebuild. Use ${TOOL_NAMES.createBlankVideo} to start a new video first.`,
                isSuccess: false,
            };
        }

        const luminary = this.luminary;
        const templateId = args.template_id ?? luminary.template.id;
        const aspectRatio = args.aspect_ratio ?? luminary.aspectRatio;

        if (templateId === (GlobalTemplate.GLOBAL_TEMPLATE_ID as string)) {
            return {
                result: `"${templateId}" is a base template with no scene designs of its own and can't be used directly. Pick one of the real templates listed in the system message.`,
                isSuccess: false,
            };
        }

        if (args.template_id != null && args.template_id === luminary.template.id) {
            return {
                result: `The video is already on template "${templateId}", so rebuilding to it just wipes the build for nothing. Edit the current video in place instead (to start over on this template, omit template_id).`,
                isSuccess: false,
            };
        }

        // One lookup validates the id and yields its supported aspect ratios. The prompt
        // template list is a starting point, not an allowlist: the agent is told to reach
        // past it with find_templates, so honour any real, accessible template and only
        // reject a genuinely non-existent id.
        const template = await findAccessibleTemplate(templateId);
        if (!template) {
            return {
                result: `Invalid template_id "${templateId}". Use a template from the system message or one returned by the ${TOOL_NAMES.findTemplates} tool.`,
                isSuccess: false,
            };
        }

        const supportedAspectRatios = template.supported_aspect_ratios;
        if (supportedAspectRatios.length > 0 && !supportedAspectRatios.includes(aspectRatio)) {
            return {
                result: `Template "${templateId}" does not support the "${aspectRatio}" aspect ratio. It supports: ${supportedAspectRatios.join(', ')}. Pick one of those, or a different template.`,
                isSuccess: false,
            };
        }

        try {
            // Use the manager's state accessor (not this tool's) so the snapshot's coverage fidelity
            // matches what the manager captures later — otherwise a voicedriven scene reads as a phantom diff.
            const captureGetState = context.videoStateManager
                ? context.videoStateManager.getStateAccessor()
                : this.getState;

            await this.dispatch(
                switchLuminaryTemplateInPlace({ luminary, templateId, aspectRatio }),
            );

            const brandKitId = selectSketchControlsBrandKitId(this.getState());
            if (brandKitId) {
                await this.dispatch(loadAndApplyBrandKitToLuminary(luminary, brandKitId));
            }

            analytics.track('Sketch: Video Template Rebuilt', {
                template_id: templateId,
                aspect_ratio: aspectRatio,
                brand_kit_id: brandKitId,
            });

            // Purely a display cue, so never let it fail a rebuild that has already
            // switched the template successfully.
            try {
                this.markRebuiltScenesPending();
            } catch (error) {
                ErrorReporter.capture(error, { extra: { context: 'mark_rebuilt_scenes_pending' } });
            }

            if (!isLuminaryEmpty(luminary)) {
                this.dispatch(
                    checkpointVideo({
                        video_data: saveRawLuminary(luminary),
                        systemTriggered: false,
                        parentActionGroup: context.parentActionGroup,
                    }),
                );
            }

            const includeColorNames = context.videoStateManager?.getIncludeColorNames() ?? true;
            const snapshot = await captureVideoState(
                luminary,
                undefined,
                captureGetState,
                includeColorNames,
            );
            context.videoStateManager?.setSnapshot(snapshot);

            const hasPreservedNarration = luminary.sceneGroups.some(isVoiceDrivenSceneGroup);
            const confirmation = hasPreservedNarration
                ? `Switched to template "${templateId}". Voice-driven narration was preserved (original audio + transcript). Music and aspect ratio carried over. Rebuild the rest under the new template.`
                : `Switched to template "${templateId}" and reset to a single blank scene. Music and aspect ratio carried over. Rebuild it under the new template.`;

            // A rebuild is a full reset, so show the entire new state (not a diff), in the host's
            // addressing mode so it matches later reads (scene_id for the toolbox, index for legacy).
            const sceneAddressing = context.videoStateManager?.getSceneAddressing() ?? 'index';
            const startingState = formatInitialStateAsGitDiff(snapshot, sceneAddressing);

            return {
                result: `${confirmation}\n\n## Current video — your starting point\n\n${startingState}`,
                isSuccess: true,
            };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return { result: `Error rebuilding video: ${errorMessage}`, isSuccess: false };
        }
    }

    /**
     * A rebuild clears the video and leaves behind scenes the agent has yet to fill:
     * either the single blank scene of a pure-text rebuild, or the base scenes of any
     * recreated voice-driven narration. Mark them so they read as pending rather than
     * sitting there looking finished while the agent rebuilds around them — matching
     * what the scene-creating tools do for the scenes they add.
     */
    private markRebuiltScenesPending(): void {
        const state = this.getState();
        for (const scene of this.luminary.scenes) {
            if (isSceneAnOverlay(state, scene)) {
                continue;
            }
            const sceneGroup = this.luminary.sceneGroups.find((group) =>
                group.scenes.some((groupScene) => groupScene.id === scene.id),
            );
            // A voicedriven base scene carries the voice asset rather than editable
            // text/media, so no fill ever arrives to clear it; blur it for the turn
            // instead of tracking it as building.
            if (sceneGroup && isVoiceDrivenSceneGroup(sceneGroup)) {
                markScenesBusy(this.dispatch, [scene.id]);
                continue;
            }
            const { needsText, needsMedia, needsIcon } =
                VideoStateTool.getSceneFillRequirements(scene);
            this.dispatch(
                startSceneBuilding({ sceneId: scene.id, needsText, needsMedia, needsIcon }),
            );
        }
    }
}
